import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Award, CheckCircle, XCircle, Loader2, Trophy, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JoinEventScan() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);

  useEffect(() => {
    checkAuthAndFetchEvent();
  }, [eventId]);

  const checkAuthAndFetchEvent = async () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    // Check if user is logged in
    if (!token || role !== 'customer') {
      // Redirect to login with return URL
      localStorage.setItem('returnUrl', `/join/event/${eventId}`);
      navigate('/login');
      return;
    }

    // Fetch event details
    try {
      const eventResponse = await axios.get(`${API}/public/event/${eventId}`);
      setEvent(eventResponse.data);

      // Fetch business details
      const businessResponse = await axios.get(`${API}/public/business/${eventResponse.data.business_id}`);
      setBusiness(businessResponse.data);

      // Check membership status
      try {
        const token = localStorage.getItem('token');
        const membershipResponse = await axios.get(`${API}/events/${eventId}/membership-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (membershipResponse.data.status !== 'not_member') {
          setMembershipStatus(membershipResponse.data.membership);
        }
      } catch (error) {
        // User is not a member
        setMembershipStatus(null);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      setError(error.response?.data?.detail || 'Event not found or not available');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinEvent = async () => {
    try {
      setJoining(true);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API}/events/join?event_id=${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Join request sent successfully! You will be notified once approved.');
      navigate('/customer/events');
    } catch (error) {
      console.error('Error joining event:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already')) {
        toast.info('You are already a member or have a pending request');
        navigate('/customer/events');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to join event');
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600 font-outfit">Loading event details...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 max-w-md w-full">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-playfair text-xl sm:text-2xl font-bold text-center mb-4 text-gray-900">Event Not Found</h2>
          <p className="text-center text-gray-600 font-outfit mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="w-full" variant="outline">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  // If already a member
  if (membershipStatus && membershipStatus.approval_status === 'approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 max-w-md w-full">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-center mb-2 text-gray-900">
            Already Joined!
          </h2>
          <p className="text-center text-gray-600 font-outfit mb-6">
            You are already a member of this event.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/customer/joined-events')}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              View My Events
            </Button>
            <Button
              onClick={() => navigate('/customer/events')}
              variant="outline"
              className="w-full"
            >
              Discover More Events
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // If pending approval
  if (membershipStatus && membershipStatus.approval_status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 max-w-md w-full">
          <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-center mb-2 text-gray-900">
            Request Pending
          </h2>
          <p className="text-center text-gray-600 font-outfit mb-6">
            Your join request is pending approval from the business.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/customer/events')}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              Discover More Events
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4">
      <Card className="p-6 sm:p-8 max-w-lg w-full">
        <Trophy className="w-16 h-16 text-amber-600 mx-auto mb-4" />
        <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-center mb-2 text-gray-900">
          {event.event_name}
        </h2>
        <p className="text-center text-gray-600 font-outfit mb-6 text-sm sm:text-base">
          {event.event_description}
        </p>

        {/* Business Info */}
        {business && (
          <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-200">
            <p className="text-sm text-gray-600 font-outfit mb-1">Hosted by:</p>
            <p className="font-semibold text-lg text-gray-900 font-playfair">{business.business_name}</p>
          </div>
        )}

        {/* Event Details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3 text-sm font-outfit">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-gray-600">Event Period</p>
              <p className="font-semibold text-gray-900">
                {new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Only show coupon value if not hidden */}
          {!event.coupon_value_hidden && (
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-gray-600">Incentive Points</p>
                <p className="font-semibold text-gray-900">{event.coupon_value}</p>
              </div>
            </div>
          )}

          {event.prizes && event.prizes.length > 0 && (
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-gray-600 mb-2">Prizes</p>
                <div className="space-y-2">
                  {event.prizes.map((prize, idx) => (
                    <div key={idx} className="bg-white p-3 rounded border border-gray-200">
                      <p className="font-semibold text-gray-900">
                        {prize.prize_name || `Prize ${idx + 1}`} - ${prize.prize_amount}
                      </p>
                      {prize.prize_description && (
                        <p className="text-xs text-gray-600 mt-1">{prize.prize_description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="bg-amber-50 rounded-lg p-4 mb-6 border border-amber-200">
          <p className="text-sm text-gray-700 font-outfit text-center">
            <strong>Join this event</strong> to submit receipts, earn coupons, and participate in prize draws!
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleJoinEvent}
            disabled={joining}
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            {joining ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Join Event
              </>
            )}
          </Button>
          <Button
            onClick={() => navigate('/customer/events')}
            variant="outline"
            className="w-full"
          >
            Browse Other Events
          </Button>
        </div>
      </Card>
    </div>
  );
}
